import * as React from 'react';
import { PIN_BELOW_CONTAINER_ATTR } from '../../media-units/detail/pin-below-container-attr';

const OWNED_INTERACTION_SELECTOR = '[role="dialog"][aria-modal="true"], [data-unified-select-cell]';

/**
 * `PopoverContent`'s width class for the offer-picker-embedded case. Used both as the literal
 * Tailwind class and, via `parseArbitraryWidthPx`, as the pinned right-edge clamp's worst-case
 * pixel width, so the two cannot drift. Kept in the `w-[Npx]` form so parsing stays a plain
 * px-suffixed number; Tailwind still generates it because scanning is a literal-text search.
 */
export const POPOVER_WIDTH_CLASS_WITH_PICKER = 'sm:w-[420px]';

function parseArbitraryWidthPx(twClass: string): number {
  const match = /\[(\d+)px\]/.exec(twClass);
  if (!match) {
    throw new Error(`parseArbitraryWidthPx: no arbitrary px value found in "${twClass}"`);
  }
  return Number(match[1]);
}

/**
 * Compact (no offer picker) width: `w-72` stays rem-based so it scales with the user's root font
 * size, which an arbitrary pixel class would drop. Its pixel twin below is therefore a separate
 * constant (18rem at a 16px root), verified against the rendered class by the responsive-width
 * test instead of by construction.
 */
export const POPOVER_WIDTH_CLASS_COMPACT = 'sm:w-72';
const POPOVER_WIDTH_COMPACT_PX = 288;

/** `undefined` (Radix's own shift handles the right edge) unless the caller opted into `pinBelowAnchor`. */
export function resolvePopoverMaxWidthPx(
  pinBelowAnchor: boolean | undefined,
  hasOfferPicker: boolean,
): number | undefined {
  if (!pinBelowAnchor) return undefined;
  return hasOfferPicker
    ? parseArbitraryWidthPx(POPOVER_WIDTH_CLASS_WITH_PICKER)
    : POPOVER_WIDTH_COMPACT_PX;
}

/**
 * `undefined` while a sibling overlay portalled to `document.body` is open: Radix's
 * dismissable-layer would otherwise treat a click or Escape inside that overlay as "outside" this
 * Popover and clear the selection before the overlay's own handler runs.
 */
export function resolveOutsideDismissHandler(
  suppress: boolean,
  onClear: () => void,
): (() => void) | undefined {
  return suppress ? undefined : onClear;
}

export function resolvePointerDownOutsideHandler(
  suppress: boolean,
  onClear: () => void,
): ((event: CustomEvent<{ originalEvent: PointerEvent }>) => void) | undefined {
  if (suppress) return undefined;
  return (event) => {
    const target = event.detail.originalEvent.target;
    if (target instanceof Element) {
      // Dialog buttons and grid cells own their resulting state transition. Clearing here can
      // race with that transition and erase a confirmed or newly replaced selection.
      if (target.closest(OWNED_INTERACTION_SELECTOR)) return;
    }
    onClear();
  };
}

export function computeSuppressOutsideClose(
  confirmingRelease: boolean,
  optionDialogOpen: boolean,
  reserveDialogOpen: boolean,
  suppressOutsideDismiss: boolean,
): boolean {
  return confirmingRelease || optionDialogOpen || reserveDialogOpen || suppressOutsideDismiss;
}

/**
 * `pinBelowAnchor` fixes `side="bottom"` and disables Radix's collision avoidance so the popover
 * can never flip over the calendar, its legend or the card title above it. Otherwise Radix's
 * default shift and flip stay on, which the catalogue and offer-draft surfaces rely on.
 */
export function resolvePopoverCollisionProps(pinBelowAnchor: boolean | undefined): {
  side: 'bottom' | undefined;
  avoidCollisions: boolean;
} {
  return pinBelowAnchor
    ? { side: 'bottom', avoidCollisions: false }
    : { side: undefined, avoidCollisions: true };
}

function resolvePinnedTop(cellElement: HTMLElement, cellBottom: number): number {
  const container = cellElement.closest<HTMLElement>(`[${PIN_BELOW_CONTAINER_ATTR}]`);
  return container ? container.getBoundingClientRect().bottom : cellBottom;
}

/**
 * Style for a zero-height fixed span at the bottom edge of the anchor cell; Radix floats the
 * popover below it. Recomputed on resize/scroll and whenever the anchor cell's identity or
 * `anchorVersion` changes (`anchorRef` itself is a stable object, so depending on it alone would
 * only run once).
 *
 * `maxPopoverWidthPx` is set only for the pinned case, where disabling `avoidCollisions` also
 * disables Radix's horizontal shift: `left` is clamped here to keep the popover inside the
 * viewport, and `top` comes from the bottom of the caller's tagged container
 * (`PIN_BELOW_CONTAINER_ATTR`) instead of the cell, so the popover clears the legend row.
 * A detached anchor's all-zero rect is skipped rather than snapping to the viewport corner.
 */
export function useAnchorPositionStyle(
  anchorRef: React.RefObject<HTMLElement | null>,
  anchorUnitId: string,
  anchorPeriodId: string,
  anchorVersion: number,
  maxPopoverWidthPx: number | undefined,
): React.CSSProperties {
  const [anchorStyle, setAnchorStyle] = React.useState<React.CSSProperties>({});

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      if (maxPopoverWidthPx === undefined) {
        setAnchorStyle({
          position: 'fixed',
          top: rect.bottom,
          left: Math.max(8, rect.left),
          width: rect.width,
          height: 0,
          pointerEvents: 'none',
        });
        return;
      }
      setAnchorStyle({
        position: 'fixed',
        top: resolvePinnedTop(anchorRef.current, rect.bottom),
        left: Math.min(
          Math.max(8, rect.left),
          Math.max(8, window.innerWidth - maxPopoverWidthPx - 8),
        ),
        width: rect.width,
        height: 0,
        pointerEvents: 'none',
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- anchorRef itself is intentionally excluded (stable identity)
  }, [anchorUnitId, anchorPeriodId, anchorVersion, maxPopoverWidthPx]);

  return anchorStyle;
}
